<template>
  <div>
    <el-popover
        v-if="popover"
        placement="left"
        width="380"
        trigger="hover">
      <DiagnosticContent
          :timestamp="reportTimeStamp"
          :fields="diagnostic" />
      <el-button
          slot="reference"
          type="text"
          style="padding: 0"
          :disabled="!found">
        <span :class="textCss">{{ btnText }}</span>
      </el-button>
    </el-popover>
    <DiagnosticContent
        v-else
        :timestamp="reportTimeStamp"
        :fields="diagnostic" />
  </div>
</template>

<script type="text/jsx">
import Vue from "vue";
import { isEmpty } from "lodash";

let DiagnosticContent = Vue.component("DiagnosticContent", {
  props: {
    fields: {
      type: Array,
      required: true,
    },
    timestamp: {
      type: String,
      default: "",
    },
  },
  computed: {
    diagnosticNotFound() {
      return isEmpty(this.fields);
    },
    diagnosticFound() {
      return !isEmpty(this.fields);
    },
  },
  methods: {
    value(field) {
      let result,
        isFirmware = field.label.toLowerCase() === "firmware",
        itNeedsAttention = field.indicator === "needs_attention",
        indicatorIsNone =
          field.indicator === "none" || field.indicator === "unknown",
        isNonHumanReadable =
          String(field["human_readable"]).toLowerCase() === "n/a",
        valueIsNull = field.value === null;

      if (isFirmware) {
        result = itNeedsAttention ? "Check Firmware" : field.human_readable;
      } else if (isNonHumanReadable) {
        /* No human readable field is assumed to be a field for which -
         * only the label is shown and it is a Yes/No field, i.e Either one it needs attention OR it does not.
         * */
        if (indicatorIsNone) result = "No";
        if (itNeedsAttention) result = "Yes";
      } else result = field.human_readable;

      return valueIsNull ? "Ø" : result;
    },
    valueCss(field) {
      return ["diagnostic-field-value", `indicator-${field.indicator}`];
    },
  },

  render: function (h) {
    let diagnosticFields = () => {};
    return (
      <div class="diagnostic-health-popover">
        <h3 style="margin-top: 0">Diagnostic Health</h3>
        {this.diagnosticNotFound ? (
          <el-table data={[]} style="width: 100%" />
        ) : (
          <div class="grid gap-h_s">
            {this.fields.map((field) => {
              return (
                <div class="span-12">
                  <div class="flex justify-between">
                    <label title={field.tooltip}>{field.label}</label>
                    <div>
                      <span class={this.valueCss(field)}>
                        {this.value(field)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {this.diagnosticFound ? (
          <div class="flex justify-center">
            <time-zone-formatter
              class="report-timestamp"
              current-time={this.timestamp}
            ></time-zone-formatter>
          </div>
        ) : null}
      </div>
    );
  },
});

export default {
  name: "DiagnosticHealth",
  components: {
    DiagnosticContent,
  },
  props: {
    popover: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: "",
    },
    reportTimeStamp: {
      type: String,
      default: "",
    },
    health: {
      type: Object,
      default() {
        return {};
      },
    },
    diagnostic: {
      type: Array,
      default() {
        return [];
      },
      validator(diagnostic) {
        const len = diagnostic.length;
        if (len) {
          const sample = diagnostic[0];
          return (
            sample.hasOwnProperty("value") &&
            sample.hasOwnProperty("label") &&
            sample.hasOwnProperty("human_readable") &&
            sample.hasOwnProperty("indicator")
          );
        }
        return true; // empty array is accepted but the component won't be shown
      },
    },
  },
  data() {
    return {};
  },
  computed: {
    found() {
      return this.diagnostic.length > 0;
    },
    textCss() {
      const indicators = {
        "Device Health": "text-needs_attention",
        Alert: "text-alert",
        Good: "text-good",
        "-": "text-not-found",
      };
      return ["diagnostic-btn-text", indicators[this.btnText]];
    },
    btnText() {
      if (this.found === false) return "No info";
      let indicators = this.diagnostic.map((field) => field.indicator);

      const needsAttention = indicators.find((i) => i === "needs_attention");
      if (needsAttention) return "Device Health";

      const alert = indicators.find((i) => i === "alert");
      if (alert) return "Alert";

      return "Good";
    },
  },
  mounted: function () {
    this.addDiagnostic(
      "egress_tx_rate",
      "Wi-Fi Transmit Speed",
      "egress_tx_rate_category",
      "Data traveling within the location."
    );
    this.addDiagnostic(
      "current_bandwidth_mb",
      "Internet Upload Speed",
      "current_bandwidth_category",
      "Data sent out to the internet"
    );
  },
  methods: {
    addDiagnostic(key, label, indicatorKey, tooltip) {
      if (this.health && key in this.health) {
        const value = this.health[key];
        const indicator = this.health[indicatorKey] || "none";

        this.diagnostic.push({
          value: value,
          label: label,
          human_readable: `${value} Mbps`,
          indicator: indicator,
          tooltip: tooltip,
        });
      }
    },
  },
};
</script>

<style lang="scss">
$good-indicator-color: #009f29;
$alert-indicator-color: #9f6000;
$needs_attention-indicator-color: #d8000c;
$color-opacity: opacity(0.9);

.diagnostic-health-popover {
  padding: 10px;

  label {
    font-size: $--font-size-small;
    font-variant: all-small-caps;
  }

  .diagnostic-field__only-label {
    border-radius: 4px;
    padding: 1px 7px;
    font-size: $--font-size-small;
  }

  .diagnostic-field-value {
    display: block;
    float: right;
    @extend .diagnostic-field__only-label;
  }

  .indicator-good {
    background-color: #e6f5ea;
    color: $good-indicator-color;
    font-size: small;
  }

  .indicator-alert {
    color: $alert-indicator-color;
    background-color: #feefb3;
    font-size: small;
  }

  .indicator-poor {
    color: $alert-indicator-color;
    background-color: #feefb3;
    font-size: small;
  }

  .indicator-needs_attention {
    color: $needs_attention-indicator-color;
    background-color: #ffd2d2;
    font-size: small;
  }

  .indicator-very_poor {
    color: $needs_attention-indicator-color;
    background-color: #ffd2d2;
    font-size: small;
  }
}

.diagnostic-btn-text {
  word-break: break-word;
  font-size: 14px;
  filter: $color-opacity;

  &.text-good {
    color: $good-indicator-color !important;
  }

  &.text-alert {
    color: $alert-indicator-color !important;
  }

  &.text-needs_attention {
    color: $--color-primary !important;
  }

  &.text-not-found {
    color: $--color-text-regular !important;
  }
}

.report-timestamp {
  font-size: 14px;
  margin: 20px 0 0 0;
  color: #909399;
}
</style>
